import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  Card,
  Title,
  Text,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@tremor/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FlagOff, RotateCcw, CheckCircle2, Archive } from 'lucide-react';

import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmModal } from '@/components/ConfirmModal';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage, getAccessToken } from '@/lib/api';

interface CaseFile {
  kind: string;
  path: string;
  size: number | null;
  mtime: string | null;
  exists: boolean;
}

interface PipelinePhase {
  phase: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  at: string | null;
  detail: string | null;
}

interface CaseDetail {
  processo: {
    id: number;
    filename: string | null;
    filepath: string | null;
    status: string | null;
    status_message: string | null;
    created_at: string | null;
    user_id: string | null;
    org_id: number | null;
    created_by: number | null;
    pdf_calculo_path: string | null;
    pjc_calculo_path: string | null;
    nota_calculo: string | null; // texto EXATO mostrado ao usuário no upload ("Observações do cálculo")
    nota_tom: string | null; // 'ok' | 'atencao' | 'erro'
    thumb: string | null; // 'up' | 'down' | null
  };
  files: CaseFile[];
  variables: Record<string, unknown>[];
  verbas: Record<string, unknown>[];
  logs: Record<string, unknown>[];
  pipeline_phases: PipelinePhase[];
}

type ModalKind = null | 'rerun' | 'force' | 'mark_test';

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

function fmtBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function phaseColor(s: PipelinePhase['status']): string {
  switch (s) {
    case 'done':
      return 'bg-emerald-500';
    case 'running':
      return 'bg-blue-500 animate-pulse';
    case 'failed':
      return 'bg-red-500';
    default:
      return 'bg-slate-600';
  }
}

export function CaseDetailPage() {
  const params = useParams({ strict: false }) as { caseId?: string };
  const caseId = Number(params.caseId);
  const qc = useQueryClient();

  const detailQuery = useQuery<CaseDetail>({
    queryKey: ['case.detail', caseId],
    queryFn: async () => (await api().get(`/cases/${caseId}`)).data,
    enabled: Number.isFinite(caseId),
  });

  const [modal, setModal] = useState<ModalKind>(null);
  const [rerunPhase, setRerunPhase] = useState<'A' | 'B' | 'C'>('A');
  const [actionError, setActionError] = useState<string | null>(null);

  const rerunMutation = useMutation({
    mutationFn: async () =>
      (await api().post(`/cases/${caseId}/rerun`, { from_phase: rerunPhase })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case.detail', caseId] });
      setModal(null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao agendar rerun.')),
  });

  const forceMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) =>
      (await api().post(`/cases/${caseId}/force-complete`, { reason })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case.detail', caseId] });
      setModal(null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao forçar conclusão.')),
  });

  const markTestMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) =>
      (await api().post(`/cases/${caseId}/mark-test`, { reason })).data,
    onSuccess: () => {
      setModal(null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao marcar como teste.')),
  });

  if (!Number.isFinite(caseId)) {
    return <Text>ID inválido.</Text>;
  }

  const detail = detailQuery.data;

  function downloadFile(kind: string) {
    // We need the access token in the Authorization header; the simplest
    // way to download a streamed FastAPI FileResponse with auth is via
    // fetch + blob. The audit log entry is written backend-side.
    const token = getAccessToken();
    void (async () => {
      try {
        const r = await api().get(`/cases/${caseId}/files/${kind}`, {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        // O FileResponse já manda o nome real (com extensão) no
        // Content-Disposition; sem extensão o SO não sabe abrir o arquivo.
        const cd = String(
          (r.headers as Record<string, string>)?.['content-disposition'] ?? '',
        );
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        const fallbackExt =
          { original: 'pdf', generated: 'pdf', pjc: 'pjc', trace: 'json' }[kind] ?? 'bin';
        const name = m
          ? decodeURIComponent(m[1])
          : `case-${caseId}-${kind}.${fallbackExt}`;
        const url = URL.createObjectURL(r.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert(extractErrorMessage(err, 'Download falhou.'));
      }
    })();
  }

  return (
    <div className="space-y-6">
      <Link to="/cases" className="text-xs text-slate-400 hover:text-slate-200">
        ← Todos os cases
      </Link>

      {detailQuery.isLoading && <LoadingSkeleton rows={6} />}

      {detailQuery.isError && (
        <div className="rounded border border-red-900/40 bg-red-950/30 p-3 text-sm text-red-300">
          Falha ao carregar case.
        </div>
      )}

      {detail && (
        <>
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Title>Case #{detail.processo.id}</Title>
                  <StatusBadge
                    status={(detail.processo.status ?? '').toLowerCase()}
                  />
                </div>
                <Text className="text-sm">
                  {detail.processo.filename ?? '(sem nome)'}
                </Text>
                <Text className="text-xs text-slate-500">
                  Criado {fmtDateTime(detail.processo.created_at)} ·{' '}
                  {detail.processo.status_message ?? 'sem mensagem'}
                </Text>

                <div className="mt-3 flex items-center gap-3">
                  {detail.pipeline_phases.map((p) => (
                    <div key={p.phase} className="flex items-center gap-1.5">
                      <span
                        className={`inline-block h-3 w-3 rounded-full ${phaseColor(
                          p.status,
                        )}`}
                        aria-label={`${p.phase}: ${p.status}`}
                        title={`${p.phase}: ${p.status}${p.detail ? ` — ${p.detail}` : ''}`}
                      />
                      <span className="text-xs text-slate-400">{p.phase}</span>
                    </div>
                  ))}
                </div>

                {/* Observações do cálculo — EXATAMENTE o texto que o usuário vê na página de upload */}
                {detail.processo.nota_calculo && (
                  <div
                    className={`mt-4 max-w-2xl rounded-md border px-3 py-2 text-sm ${
                      detail.processo.nota_tom === 'erro'
                        ? 'border-rose-700/50 bg-rose-900/20 text-rose-200'
                        : detail.processo.nota_tom === 'atencao'
                          ? 'border-amber-700/50 bg-amber-900/20 text-amber-100'
                          : 'border-slate-700 bg-slate-900/40 text-slate-200'
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                      <span>Observações do cálculo (visto pelo usuário)</span>
                      {detail.processo.thumb === 'up' && (
                        <span title="Usuário curtiu" className="text-emerald-400">
                          👍
                        </span>
                      )}
                      {detail.processo.thumb === 'down' && (
                        <span title="Usuário NÃO curtiu" className="text-rose-400">
                          👎
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-line">{detail.processo.nota_calculo}</p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setModal('rerun');
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-600/20 px-3 py-1.5 text-sm text-blue-300 hover:bg-blue-600/30"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Rerun
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setModal('force');
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600/20 px-3 py-1.5 text-sm text-emerald-300 hover:bg-emerald-600/30"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Force-complete
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setModal('mark_test');
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                >
                  <FlagOff className="h-3.5 w-3.5" /> Marcar como teste
                </button>
                <button
                  type="button"
                  disabled
                  title="Disponível na Phase 2"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-500"
                >
                  <Archive className="h-3.5 w-3.5" /> Download all (Phase 2)
                </button>
              </div>
            </div>
          </Card>

          <TabGroup>
            <TabList>
              <Tab>Files</Tab>
              <Tab>Variables</Tab>
              <Tab>Verbas</Tab>
              <Tab>Logs</Tab>
              <Tab>Pipeline</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <FilesTab
                  files={detail.files}
                  onDownload={downloadFile}
                />
              </TabPanel>
              <TabPanel>
                <DictTab rows={detail.variables} empty="Sem variáveis para este case." />
              </TabPanel>
              <TabPanel>
                <DictTab rows={detail.verbas} empty="Sem verbas extraídas." />
              </TabPanel>
              <TabPanel>
                <DictTab rows={detail.logs} empty="Sem logs no audit trail para este case." />
              </TabPanel>
              <TabPanel>
                <PipelineTab phases={detail.pipeline_phases} />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </>
      )}

      <ConfirmModal
        open={modal === 'rerun'}
        title="Reagendar processamento"
        description="Marca o case como QUEUED para o worker reprocessar a partir da fase escolhida."
        confirmLabel="Rerun"
        busy={rerunMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={() => rerunMutation.mutate()}
      >
        <label className="block text-sm">
          <span className="mb-1 block text-slate-300">A partir da fase</span>
          <select
            value={rerunPhase}
            onChange={(e) => setRerunPhase(e.target.value as 'A' | 'B' | 'C')}
            className="block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 focus:border-blue-500 focus:outline-none"
          >
            <option value="A">A — Extração</option>
            <option value="B">B — Playwright / PJe-Calc</option>
            <option value="C">C — PDF final</option>
          </select>
        </label>
      </ConfirmModal>

      <ConfirmModal
        open={modal === 'force'}
        title="Forçar conclusão"
        description="Marca o case como DONE manualmente. Use apenas se você tem um PDF correto fora do pipeline."
        requireReason
        confirmLabel="Force-complete"
        destructive
        busy={forceMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={(reason) => forceMutation.mutate({ reason })}
      />

      <ConfirmModal
        open={modal === 'mark_test'}
        title="Marcar como teste"
        description="Não altera o registro no banco — apenas grava no audit log que este case é de teste (ex: corpus F5 v3)."
        requireReason
        confirmLabel="Marcar"
        busy={markTestMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={(reason) => markTestMutation.mutate({ reason })}
      />
    </div>
  );
}

function FilesTab({
  files,
  onDownload,
}: {
  files: CaseFile[];
  onDownload: (kind: string) => void;
}) {
  return (
    <Card className="overflow-hidden p-0">
      <table className="min-w-full text-sm text-slate-200">
        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Tipo</th>
            <th className="px-3 py-2 text-left">Caminho</th>
            <th className="px-3 py-2 text-right">Tamanho</th>
            <th className="px-3 py-2 text-left">Modificado</th>
            <th className="px-3 py-2 text-right">Ação</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.kind} className="border-t border-slate-800">
              <td className="px-3 py-2">{f.kind}</td>
              <td className="px-3 py-2 text-xs text-slate-400">{f.path || '—'}</td>
              <td className="px-3 py-2 text-right text-xs text-slate-400">
                {fmtBytes(f.size)}
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {fmtDateTime(f.mtime)}
              </td>
              <td className="px-3 py-2 text-right">
                {f.exists ? (
                  <button
                    type="button"
                    onClick={() => onDownload(f.kind)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                  >
                    <Download className="h-3 w-3" /> baixar
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">indisponível</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function DictTab({
  rows,
  empty,
}: {
  rows: Record<string, unknown>[];
  empty: string;
}) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-700 bg-slate-900/20 p-6 text-center text-sm text-slate-400">
        {empty}
      </div>
    );
  }
  const keys = Object.keys(rows[0]);
  return (
    <Card className="overflow-x-auto p-0">
      <table className="min-w-full text-sm text-slate-200">
        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
          <tr>
            {keys.map((k) => (
              <th key={k} className="px-3 py-2 text-left">
                {k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-800">
              {keys.map((k) => (
                <td key={k} className="px-3 py-2 align-top text-xs text-slate-300">
                  <DictCell value={r[k]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function DictCell({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-slate-600">—</span>;
  if (typeof value === 'object') {
    return (
      <pre className="max-w-md whitespace-pre-wrap break-words text-[10px] text-slate-400">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <>{String(value)}</>;
}

function PipelineTab({ phases }: { phases: PipelinePhase[] }) {
  return (
    <Card>
      <ol className="space-y-4">
        {phases.map((p) => (
          <li key={p.phase} className="flex gap-3">
            <span
              className={`mt-1 inline-block h-3 w-3 shrink-0 rounded-full ${phaseColor(
                p.status,
              )}`}
            />
            <div>
              <p className="text-sm font-medium text-slate-100">{p.phase}</p>
              <p className="text-xs text-slate-400">
                <StatusBadge status={p.status} className="mr-2" />
                {p.detail ?? '—'}
              </p>
              {p.at && (
                <p className="text-[10px] text-slate-500">{fmtDateTime(p.at)}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
